import _ from 'underscore'
import React, { useState } from 'react'

import EditableComponent from 'pages/Device/SystemComponentsPanel/EditableComponent'
import DisplayComponent from 'pages/Device/SystemComponentsPanel/DisplayComponent'

import Api from 'services/api'
import Apptentive from 'helpers/apptentive'

import { SystemComponent } from 'types/SystemComponent'

import { useConfirmDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

export type Props ={
  component: Required<SystemComponent>
  deviceId: string
  onCancelCallBack: (arg: Required<SystemComponent>) => void
  onRefreshCallBack: () => void
  setEditCallBack: (arg?:Required<SystemComponent>) => void
  editableComponent: Required<SystemComponent>
  disabled: boolean
}

const Component = ({ disabled, editableComponent, component, deviceId, onCancelCallBack, onRefreshCallBack, setEditCallBack: setEditCallback }: Props) => {
  const [newComponent, setNewComponent] = useState<SystemComponent>(component)
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)
  const { confirm } = useConfirmDialog()
  const componentType = 'System Component'

  const onSave = () => {
    setIsLoading(true)
    setShowSpinner(true)
    if (newComponent.modelNumber.length) {
      if (newComponent.id) {
        Api.SystemComponents.update(deviceId, newComponent.id, newComponent).then(() => {
          onRefreshCallBack()
          setIsLoading(false)
          setShowSpinner(false)
        })
      } else {
        Api.SystemComponents.create(deviceId, newComponent).then(() => {
          Apptentive.sendEvent('add_system_component_saved')

          onRefreshCallBack()
          setIsLoading(false)
          setShowSpinner(false)
        })
      }
      setErrorMessage('')
    } else {
      setErrorMessage('Model Number Required')
      setIsLoading(false)
      setShowSpinner(false)
    }
  }

  const onCancel = async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })
    if (newComponent.id) {
      if (!_.isMatch(component, newComponent)) {
        if (isConfirmed) {
          setNewComponent(component)
        }
      }
      setEditCallback(undefined)
    } else {
      if (!_.isMatch(component, newComponent)) {
        if (isConfirmed) {
          onCancelCallBack(component)
        }
      } else {
        onCancelCallBack(component)
      }
    }
  }

  const onDelete = async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
      description:
        CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
    })

    if (isConfirmed) {
      setIsDeleting(true)
      setShowSpinner(true)
      Api.SystemComponents.delete(deviceId, newComponent.id).then(() => {
        setIsDeleting(false)
        onRefreshCallBack()
        setShowSpinner(false)
      })
    }
  }

  const onChange = (updateComponent: Partial<SystemComponent>) => {
    if (newComponent) {
      const modifiedComponent : SystemComponent = { ...newComponent, ...updateComponent }
      setNewComponent({ ...modifiedComponent })
    }
  }

  const onEdit = () => {
    setEditCallback(component)
  }

  return (
    <>
      { _.isMatch(component, editableComponent)
        ? <EditableComponent
            errorMessage={errorMessage}
            loading={loading}
            isDeleting={isDeleting}
            content={newComponent}
            onChangeCallBack={onChange}
            onCancelCallBack={onCancel}
            onSaveCallBack={onSave}
            onDeleteCallBack={onDelete}
            showSpinner={showSpinner}
          />
        : <DisplayComponent disabled={disabled} content={newComponent} onEditCallBack={onEdit} showSpinner={showSpinner} />
      }
    </>
  )
}

export default Component
